import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { Peel, supportsHtmlInCanvas } from '@/components/canvasui/Peel';
import { cn } from '@/lib/utils';

import { useSidebar } from './sidebar-context';

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = React.useState(() => window.matchMedia(query).matches);
  React.useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

/**
 * What the peel reveals: the real sidebar column at the left edge, and the
 * page background past it so an over-peel never exposes a raw white box.
 */
function UnderSidebar({ panel }: { panel: React.ReactNode }) {
  return (
    <div className="flex h-full w-full bg-bg">
      <div className="h-full w-60 shrink-0 border-r border-line bg-surface">{panel}</div>
    </div>
  );
}

/**
 * Slide-in fallback for browsers without HTML-in-canvas: same trigger (cursor
 * at the left edge), same revealed sidebar, minus the curl.
 */
function EdgeRevealOverlay({ panel }: { panel: React.ReactNode }) {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  // Navigating from the overlay means the reveal did its job; tuck it away.
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Position-based close, not just onMouseLeave: the overlay slides in UNDER
  // a stationary cursor, and the browser only recomputes enter/leave pairs on
  // the next mousemove — so a leave can be missed entirely if the pointer
  // never moved while inside. Watching the x position is airtight.
  React.useEffect(() => {
    if (!open) return;
    const onMove = (event: MouseEvent) => {
      if (event.clientX > 248) setOpen(false);
    };
    window.addEventListener('mousemove', onMove);
    return () => window.removeEventListener('mousemove', onMove);
  }, [open]);

  return (
    <>
      <div
        className="absolute inset-y-0 left-0 z-40 w-1.5"
        onMouseEnter={() => setOpen(true)}
        aria-hidden
      />
      <div
        className={cn(
          'absolute inset-y-0 left-0 z-50 w-60 border-r border-line bg-surface shadow-pop',
          'transition-transform duration-200 ease-out',
          open ? 'translate-x-0' : 'pointer-events-none -translate-x-full',
        )}
        onMouseLeave={() => setOpen(false)}
      >
        {panel}
      </div>
    </>
  );
}

/**
 * Wraps the app's content column (header + main).
 *
 * Expanded sidebar, or any viewport below `lg`: a transparent wrapper — the
 * sidebar behaves normally. Collapsed on desktop: the sidebar column is gone
 * entirely and this wrapper takes over the left edge. In browsers with
 * HTML-in-canvas the page itself becomes the peeling sheet (canvas-ui Peel,
 * `side="left"`): moving the cursor to the edge curls the live page back and
 * reveals the real sidebar underneath, fully interactive. Everywhere else the
 * same gesture slides the sidebar in over the content instead.
 */
export function SidebarContentReveal({
  panel,
  children,
}: {
  /** The sidebar body to reveal (a `<SidebarPanel footer="pin" />`). */
  panel: React.ReactNode;
  children: React.ReactNode;
}) {
  const { collapsed } = useSidebar();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [peelSupported] = React.useState(() => supportsHtmlInCanvas());

  // `bg-bg` is load-bearing: the peel captures this column as the sheet
  // texture, and anything relying on the <body> background (which sits
  // outside the peel) would capture as TRANSPARENT — letting the under-layer
  // sidebar show through the resting page.
  const column = <div className="flex h-full min-w-0 flex-col bg-bg">{children}</div>;

  if (!collapsed || !isDesktop) {
    // `h-full` to match the other two branches: `column` sizes itself with
    // `h-full`, which only resolves against a parent with a definite height.
    return <div className="h-full min-w-0 flex-1">{column}</div>;
  }

  if (peelSupported && !reducedMotion) {
    return (
      <Peel
        side="left"
        // Hover mode peels fully on entering the zone. Cursor mode's peel
        // amount tracks pointer proximity, which settles around 55–70% as the
        // hand chases the fold — the sidebar never finished appearing.
        mode="hover"
        // THE tuning knob for how much the peel exposes: each +1 of `reveal`
        // adds ~2px of revealed width. The flipped tail lands back over the
        // revealed strip, so the exposed width is roughly
        // A·(2·(reveal + bulge) − (π−2)·curl) ≈ 370–385px here — the full
        // 240px sidebar plus a wide backdrop margin.
        reveal={265}
        zone={150}
        curl={170}
        bow={48}
        bulge={24}
        shade={0.2}
        shine={0.7}
        shineColor="auto"
        smoothing={0.3}
        under={<UnderSidebar panel={panel} />}
        className="h-full min-w-0 flex-1"
      >
        {column}
      </Peel>
    );
  }

  return (
    <div className="relative h-full min-w-0 flex-1">
      {column}
      <EdgeRevealOverlay panel={panel} />
    </div>
  );
}
